/**
 * 属性スキーマと統制語彙（**参照のみ**）。
 *
 * 正本は ETL 側の統制語彙の契約で、管理画面から書き換える口は API に無い。
 * 「編集できそうな見た目」にしないために、入力欄も保存のボタンも置かない。
 *
 * ## 空と null を「無い」で塗りつぶさない
 *
 * - `levels`（カテゴリ別の必須度）は登録先がまだ無いので**常に空配列**。
 *   「どのカテゴリでも不要」ではなく「登録がまだ無い」である
 * - `VocabularyTerm.count`（語ごとの出現件数）は数える集計が無いので**常に null**。
 *   0 件と描かない
 * - `termCount` が null なのは値域を閉じない自由属性で、語が 0 個ではない
 */

import { useQuery } from "urql";
import {
  Card,
  Disclosure,
  ErrorState,
  KnowledgeSchemaDocument,
  LoadingState,
  PageHeader,
  Tag,
} from "visumo-data-agent-web-shared";
import { HelpIcon } from "../help/HelpIcon";

import {
  ATTRIBUTE_LEVELS_NOT_REGISTERED_NOTE,
  VOCABULARY_TERM_COUNT_NOT_MEASURED_NOTE,
} from "../api-capabilities";

export function KnowledgeSchemaPage() {
  const [result, refetch] = useQuery({ query: KnowledgeSchemaDocument });

  if (result.fetching && result.data === undefined) {
    return <LoadingState />;
  }
  if (result.error !== undefined || result.data === undefined) {
    return (
      <ErrorState
        description={result.error?.message}
        onRetry={() => refetch({ requestPolicy: "network-only" })}
      />
    );
  }

  const rows = result.data.attributeSchema;
  const axes = result.data.vocabularyAxes;

  return (
    <div className="vd-page">
      <PageHeader
        title="属性スキーマと統制語彙"
        description="どの属性群にどの属性があり、その値域にどの語があるかです。この画面からは変えられません（正本は取り込みの側にあります）。"
        titleExtra={<HelpIcon slug="knowledge/schema" />}
      />

      <div className="vd-stack">
        <Card
          title="属性スキーマ"
          note={`ここに載っているのは、いま値が入りうる属性だけです。まだ集めていない属性は行そのものを作っていません（作ると「そういう属性がある」という形だけが残るため）。${ATTRIBUTE_LEVELS_NOT_REGISTERED_NOTE}`}
        >
          <div className="vd-table-scroll">
            <table className="vd-table">
              <thead>
                <tr>
                  <th>属性群</th>
                  <th>属性</th>
                  <th>統制語彙の軸</th>
                  <th>カテゴリ別の必須度</th>
                  <th>注記</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={`${row.group}-${row.attribute}`}>
                    <th scope="row">{row.group}</th>
                    <td>{row.attribute}</td>
                    <td>
                      {row.vocabAxis === null ? (
                        <Tag>軸なし（値域を閉じていません）</Tag>
                      ) : (
                        row.vocabAxis
                      )}
                    </td>
                    <td>
                      {row.levels.length === 0
                        ? "登録がまだありません"
                        : row.levels
                            .map((level) => `${level.category}: ${level.level}`)
                            .join(" / ")}
                    </td>
                    <td>{row.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="統制語彙" note={VOCABULARY_TERM_COUNT_NOT_MEASURED_NOTE}>
          <div className="vd-stack">
            {axes.map((axis) => (
              <section
                key={axis.id}
                aria-label={`語彙の軸 ${axis.label}`}
                style={{
                  borderTop: "1px solid var(--vd-border)",
                  paddingTop: 12,
                }}
              >
                <div
                  className="vd-row"
                  style={{ justifyContent: "space-between" }}
                >
                  <div className="vd-card-title">
                    {axis.label}
                    {axis.multiValued ? <Tag tone="info">複数選択</Tag> : null}
                  </div>
                  <div className="vd-stat-note">
                    {axis.termCount === null
                      ? "値域を閉じていません（語の一覧を持ちません）"
                      : `${axis.termCount.toLocaleString("ja-JP")} 語`}
                  </div>
                </div>
                {axis.origin === null ? null : (
                  <p className="vd-stat-note" style={{ margin: "4px 0 0" }}>
                    出どころ: {axis.origin}
                    {axis.unit === null ? "" : ` ・ 単位 ${axis.unit}`}
                  </p>
                )}
                {axis.terms.edges.length === 0 ? (
                  <p className="vd-stat-note" style={{ margin: "4px 0 0" }}>
                    この軸は値域を閉じていないので、語の一覧がありません。
                  </p>
                ) : (
                  <>
                    <p style={{ margin: "4px 0 0" }}>
                      {axis.terms.edges
                        .map((edge) => edge.node.term)
                        .join(" / ")}
                    </p>
                    <Disclosure summary={`「${axis.label}」の語の言い換え`}>
                      {axis.terms.edges.map((edge) => (
                        <p key={edge.node.slug} style={{ margin: "0 0 4px" }}>
                          <strong>{edge.node.term}</strong>（{edge.node.slug}）
                          {edge.node.synonyms.length === 0
                            ? " — 言い換えの登録はありません"
                            : ` — ${edge.node.synonyms.join(" / ")}`}
                        </p>
                      ))}
                    </Disclosure>
                  </>
                )}
                {axis.terms.pageInfo.hasNextPage ? (
                  <p className="vd-stat-note" style={{ margin: "4px 0 0" }}>
                    語が多いため、ここには先頭のぶんだけを出しています。
                  </p>
                ) : null}
              </section>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
